import { useMutation, useQueryClient } from '@tanstack/react-query';

import type { TStaffTransitionPayload, TStaffTransitionReport } from '@/services/staff';

import { notifyMeta } from '@/lib/notify';
import {
  APPLICANT_KEYS,
  CONCIERGE_KEYS,
  MEMBER_KEYS,
  STAFF_KEYS,
} from '@/constants/queryKeys';
import { errorUtils } from '@/services/errors';
import { staffService } from '@/services/staff';
import { toMemberHolderIDs } from '@/utils/staffTransition';
import { useSyncConciergeBooks } from '@/hooks/chat';

const applyStaffTransition = async (payload: TStaffTransitionPayload) => errorUtils.unwrap(await staffService.transitionStaff(payload));

const useApplyStaffTransition = () => {
  const queryClient = useQueryClient();
  const syncBooks = useSyncConciergeBooks();

  return useMutation<TStaffTransitionReport, Error, TStaffTransitionPayload>({
    meta: notifyMeta('staffTransition'),
    mutationKey: ['staff', 'transition'],
    mutationFn: applyStaffTransition,
    onSuccess: async (report) => {
      const holderIDs = toMemberHolderIDs(report);

      if (report.isApplied && holderIDs.length > 0) {
        syncBooks.mutate(holderIDs);
      }

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: STAFF_KEYS.root }),
        queryClient.invalidateQueries({ queryKey: CONCIERGE_KEYS.root }),
        queryClient.invalidateQueries({ queryKey: APPLICANT_KEYS.root }),
        queryClient.invalidateQueries({ queryKey: MEMBER_KEYS.root }),
      ]);
    },
  });
};

export {
  useApplyStaffTransition,
};
